import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useScoutRequestFeed } from "../../hooks/scout-request/useScoutRequestFeed";
import { useAgencyLabels } from "../../hooks/scout-request/useAgencyLabels";
import AgentRequestCard from "../../components/scout-request/AgentRequestCard";
import ScoutFilterBar from "../../components/scout-request/ScoutFilterBar";
import RecommendModal from "../../components/scout-request/RecommendModal";
import ScoutShareModal from "../../components/scout-request/ScoutShareModal";
import { formatScoutPosition } from "../../constants/scoutRequestChoices";
import ScoutInfoDot from "../../components/scout-request/ScoutInfoDot";
import { scoutAgentService } from "../../services/scoutAgentService";

const SEGMENTS = [
  { value: "", label: "All" },
  { value: "professional", label: "Professional" },
  { value: "youth", label: "Youth" },
];

const CardSkeleton = () => (
  <div className="bg-white rounded-xl p-5 border border-[#E5E7EB] animate-pulse">
    <div className="flex items-center gap-2.5 mb-3">
      <div className="w-10 h-10 rounded-full bg-gray-100" />
      <div className="flex-1">
        <div className="h-4 w-32 bg-gray-100 rounded mb-1.5" />
        <div className="h-3 w-20 bg-gray-100 rounded" />
      </div>
    </div>
    <div className="h-4 w-40 bg-gray-100 rounded mb-2" />
    <div className="h-3 w-56 bg-gray-100 rounded mb-3" />
    <div className="flex gap-2 mb-4">
      <div className="h-5 w-16 bg-gray-100 rounded-full" />
      <div className="h-5 w-20 bg-gray-100 rounded-full" />
    </div>
    <div className="h-9 w-full bg-gray-100 rounded-lg" />
  </div>
);

// Real server pagination (review N-1) — Prev/Next mirroring the club-network feed.
const Pagination = ({ pagination, onPage }) => {
  if (!pagination || pagination.count <= 0) return null;
  const { currentPage, next, previous } = pagination;
  if (!next && !previous) return null;
  return (
    <div className="flex justify-center items-center gap-4 mt-8">
      <button
        disabled={!previous}
        onClick={() => onPage(currentPage - 1)}
        className="px-4 py-2 border border-[#E5E7EB] rounded-lg text-[13px] font-semibold disabled:opacity-40 hover:bg-gray-50"
      >
        Previous
      </button>
      <span className="text-[13px] text-[#6B7280]">Page {currentPage}</span>
      <button
        disabled={!next}
        onClick={() => onPage(currentPage + 1)}
        className="px-4 py-2 border border-[#E5E7EB] rounded-lg text-[13px] font-semibold disabled:opacity-40 hover:bg-gray-50"
      >
        Next
      </button>
    </div>
  );
};

// A readable label for an active-filter pill (design 1i).
const chipLabel = ({ key, value }) => {
  if (key === "position") return formatScoutPosition(value);
  if (key === "category") return value.charAt(0).toUpperCase() + value.slice(1);
  if (key === "fee") return `Fee: ${value === "range" ? "framed" : value}`;
  return value;
};

const ScoutRequestFeed = () => {
  const navigate = useNavigate();
  const feed = useScoutRequestFeed();
  const { labels, createLabel } = useAgencyLabels();
  const [recommendFor, setRecommendFor] = useState(null);
  const [sharingRequest, setSharingRequest] = useState(null);

  const {
    requests, loading, error, fetchFeed, pagination,
    category, setCategory,
    searchTerm, setSearchTerm,
    activeFilters, clearFilter, clearAll,
    applyMark,
  } = feed;

  const hasFilters = activeFilters.length > 0 || !!searchTerm;

  return (
    <div className="min-h-screen bg-[#EDF0F4]">
      <div className="mx-auto px-4 md:px-12 py-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-[#111827]">Scout requests</h1>
            <ScoutInfoDot
              id="agent-browse"
              title="What are scout requests?"
              body="Clubs post what they're actually looking for: position, age, horizon and a realistic fee frame. Recommend a player you represent; the club sees your name with every recommendation."
            />
          </div>
          <p className="text-[13px] text-[#6B7280] mt-1 max-w-2xl">
            Clubs post what they&apos;re scouting for, openly, under their name. Your recommendations are
            visible only to the club.
          </p>
        </div>

        {/* Segmented + search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="inline-flex rounded-lg bg-white border border-[#E5E7EB] p-0.5 w-fit">
            {SEGMENTS.map((seg) => {
              const active = category === seg.value;
              return (
                <button
                  key={seg.value || "all"}
                  onClick={() => setCategory(active && seg.value ? "" : seg.value)}
                  aria-pressed={active}
                  className={`px-4 py-1.5 rounded-md text-[13px] font-semibold transition ${
                    active ? "bg-[#E8F1FF] text-[#1B6FE0]" : "text-[#6B7280] hover:text-[#111827]"
                  }`}
                >
                  {seg.label}
                </button>
              );
            })}
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search clubs, positions…"
              className="w-full bg-white border border-[#E5E7EB] rounded-lg pl-9 pr-3 py-2 text-[13px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
            />
          </div>
        </div>

        {/* Filter chips + count */}
        <div className="mb-3">
          <ScoutFilterBar {...feed} resultCount={pagination.count} />
        </div>

        {/* Active-filter pills (1i) */}
        {hasFilters && (
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {activeFilters.map((f) => (
              <button
                key={f.key}
                onClick={() => clearFilter(f.key)}
                className="inline-flex items-center gap-1 rounded-full bg-[#E8F1FF] text-[#1B6FE0] px-3 py-1 text-[12px] font-semibold"
              >
                {chipLabel(f)}
                <X className="w-3.5 h-3.5" />
              </button>
            ))}
            <button onClick={clearAll} className="text-[12.5px] font-semibold text-[#6B7280] hover:text-[#111827]">
              Clear all
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-20 text-center">
            <p className="text-red-500 mb-3">{error}</p>
            <button onClick={() => fetchFeed(1)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Try again
            </button>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
            <div className="bg-white border border-[#E5E7EB] rounded-full p-4 mb-4">
              <Search className="w-7 h-7 text-[#9CA3AF]" />
            </div>
            <h3 className="text-[17px] font-bold text-[#111827] mb-1.5">No requests match</h3>
            <p className="text-[13px] text-[#6B7280] max-w-sm mb-5">Try widening the position or horizon.</p>
            {hasFilters && (
              <button
                onClick={clearAll}
                className="px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0]"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {requests.map((r) => (
                <AgentRequestCard
                  key={r.id}
                  request={r}
                  labels={labels}
                  onRecommend={setRecommendFor}
                  onShare={setSharingRequest}
                  onMarkChange={applyMark}
                  onCreateLabel={createLabel}
                />
              ))}
            </div>
            <Pagination pagination={pagination} onPage={(p) => fetchFeed(p)} />
          </>
        )}
      </div>

      <RecommendModal
        open={!!recommendFor}
        request={recommendFor}
        onClose={() => setRecommendFor(null)}
        onViewMyRecs={() => navigate("/scout-requests/my-recommendations")}
      />

      <ScoutShareModal
        open={!!sharingRequest}
        title="Share scout request"
        onClose={() => setSharingRequest(null)}
        onSend={(channelId, text) =>
          scoutAgentService.shareRequest(sharingRequest.id, { channelId, text })
        }
      />
    </div>
  );
};

export default ScoutRequestFeed;
